import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { kpiPortalApi, portalSession } from '../lib/api';
import PortalGate from './PortalGate';
import { formatDateTime } from '../lib/utils';

// The one month an employee may file for: the month that is running. October's
// suggestion or kaizen goes in during October, up to the 31st; from 1 November
// that month is shut and only HR can record a missed one. So there is no month
// to choose any more — the form states which month it is filing for.
//
// Read from the server (participation.current) wherever possible: the phone's
// own clock and timezone must not decide which month an entry belongs to. This
// is only the fallback for the first paint, before that call returns.
function localPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function periodLabel(p) {
  const m = /^(\d{4})-(\d{2})$/.exec(p || '');
  if (!m) return p || '';
  return new Date(Number(m[1]), Number(m[2]) - 1, 1)
    .toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

// The last day of a 'YYYY-MM', for telling the employee when the window shuts.
function periodLastDay(p) {
  const m = /^(\d{4})-(\d{2})$/.exec(p || '');
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]), 0);
  return d.toLocaleString('en-US', { day: 'numeric', month: 'long' });
}

// An empty HR-F-16 kaizen, as an employee files it from their phone. Kaizen No.
// is not asked for — HR numbers the register entry.
const blankKaizen = () => ({
  subject: '', process: '', before_text: '', after_text: '',
  before_photo: null, after_photo: null, benefit_category: '', benefit_amount: '',
  benefit_notes: '',
  total_cost_saving: '', implementation_date: '', approved_by: '', implemented_by: '',
});

const MAX_PHOTO_BYTES = 3 * 1024 * 1024;

// Whatever the server refused with, as one line an employee can read. A
// body-level rejection arrives as a list of objects, and putting that straight
// on screen renders nothing useful (or breaks the page) — so the fallback is a
// plain sentence rather than the shape of the error.
const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string' && d.trim()) return d;
  if (Array.isArray(d)) {
    const first = d.find((x) => typeof x?.msg === 'string');
    if (first) return first.msg;
  }
  return fallback;
};

// Has any part of the kaizen been filled in? A wholly blank one means "I am
// only filing a suggestion", not "I filed an invalid kaizen".
const kaizenTouched = (k) => Boolean(
  k.subject.trim() || k.process.trim() || k.before_text.trim() || k.after_text.trim()
  || k.before_photo || k.after_photo || k.benefit_category || k.benefit_notes.trim()
  || k.total_cost_saving.trim() || k.approved_by.trim() || k.implemented_by.trim()
  || k.implementation_date,
);

export default function KpiPortal() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [gate, setGate] = useState(null);      // auth-state from the server
  const [authed, setAuthed] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  // Scanning the QR lands on a chooser: the monthly filing (suggestion and/or
  // kaizen, one screen) or the KPI sheet. There is no approval step — KPI
  // marks come from the source software and are final.
  const [mode, setMode] = useState(null);          // null = choose | 'file' | 'report' | 'manualkpi'
  const [month, setMonth] = useState('');
  const [card, setCard] = useState('');            // typed to confirm identity on submit
  const [scores, setScores] = useState({});
  const [sugg, setSugg] = useState('');
  // The benefit a suggestion claims — same five categories as a kaizen.
  const [suggCat, setSuggCat] = useState('');
  const [suggAmt, setSuggAmt] = useState('');
  const [kz, setKz] = useState(blankKaizen);
  const [kzHistory, setKzHistory] = useState([]);
  const [filed, setFiled] = useState(null);        // what the last Submit actually filed
  // Which months already carry a suggestion or a kaizen. One of the two is
  // required every month, and the KPI sheet is refused without it.
  const [part, setPart] = useState(null);
  const [report, setReport] = useState(null);
  // Manual KPI work: what this signed-in person must fill for their own people.
  const [mk, setMk] = useState(null);
  const [mkEdits, setMkEdits] = useState({});
  const openManualKpi = async () => {
    setErr('');
    try {
      const r = await kpiPortalApi.manualKpi(token);
      setMk(r.data); setMkEdits({}); setMode('manualkpi');
    } catch (e) { setErr(errText(e, 'Could not open your KPI work.')); }
  };
  const submitManualKpi = async () => {
    const items = Object.entries(mkEdits)
      .filter(([, v]) => v !== '' && v != null)
      .map(([k, v]) => {
        const [employee_id, kpi_id] = k.split('|');
        return { employee_id, kpi_id, score: Number(v) };
      });
    if (!items.length) { setErr('Enter at least one score.'); return; }
    setSaving(true); setErr('');
    try {
      const r = await kpiPortalApi.submitManualKpi(token, { fy: mk.fy, month: mk.month, items });
      const fresh = await kpiPortalApi.manualKpi(token);
      setMk(fresh.data); setMkEdits({});
      if (r.data.rejected?.length) {
        setErr(`${r.data.saved} saved · ${r.data.rejected.length} rejected (${r.data.rejected[0].reason})`);
      }
    } catch (e) { setErr(errText(e, 'Could not save.')); }
    finally { setSaving(false); }
  };
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = React.useCallback(() => {
    kpiPortalApi.mySuggestions(token)
      .then((r) => setHistory(r.data || []))
      .catch(() => { /* history is a nicety — never block the form */ });
  }, [token]);

  const loadKaizens = React.useCallback(() => {
    kpiPortalApi.myKaizens(token)
      .then((r) => setKzHistory(r.data || []))
      .catch(() => { /* as above */ });
  }, [token]);

  // Refreshed after every suggestion and kaizen, so the menu badge and the KPI
  // sheet's warning reflect what was just filed without a page reload.
  const loadParticipation = React.useCallback(() => {
    kpiPortalApi.participation(token)
      .then((r) => setPart(r.data))
      .catch(() => { /* the server enforces the rule regardless */ });
  }, [token]);

  // Auth first. The QR alone proves nothing, so nothing is fetched until the
  // employee has a valid session for THIS token.
  const loadGate = React.useCallback(() => {
    kpiPortalApi.authState(token)
      .then((r) => {
        setGate(r.data);
        setAuthed(r.data.state === 'needs_login' && !!portalSession.get(token));
      })
      .catch(() => setErr('This link is not valid. Please contact HR.'));
  }, [token]);

  useEffect(() => { loadGate(); }, [loadGate]);

  useEffect(() => {
    if (!authed) return;
    kpiPortalApi.get(token)
      .then((r) => {
        const m = new Date().toLocaleString('en-US', { month: 'short' });
        setInfo(r.data);
        setMonth((r.data.months || []).includes(m) ? m : (r.data.months || [])[0] || '');
        loadHistory();
        loadKaizens();
        loadParticipation();
      })
      .catch((e) => {
        // A stale or revoked session drops back to the sign-in screen rather
        // than showing a misleading "invalid link".
        if (e?.response?.status === 401) {
          portalSession.clear(token); setAuthed(false); loadGate();
        } else {
          setErr('Could not load your portal. Please try again.');
        }
      });
  }, [authed, token, loadHistory, loadKaizens, loadParticipation, loadGate]);

  const setScore = (id, k, v) => setScores((s) => ({ ...s, [id]: { ...(s[id] || {}), [k]: v } }));

  // Load the year sheet and seed the form with whatever is already saved for
  // the chosen month, so reopening the QR shows previous work rather than blanks.
  const [myKpi, setMyKpi] = useState(null);
  const openMyKpi = async (m) => {
    setErr('');
    try {
      const r = await kpiPortalApi.myKpi(token, m);
      setMyKpi(r.data);
      setMode('mykpi');
    } catch (e) {
      setErr(errText(e, 'Could not load your KPI score.'));
    }
  };
  const openReport = async () => {
    setErr('');
    try {
      const r = await kpiPortalApi.report(token);
      setReport(r.data);
      setMode('report');
    } catch (e) {
      setErr(errText(e, 'Could not open the report.'));
    }
  };

  useEffect(() => {
    if (!report || !month) return;
    const seeded = {};
    (report.rows || []).forEach((row) => {
      const c = (row.cells || {})[month] || {};
      seeded[row.kpi_id] = {
        self_score: c.marks ?? '',
        self_remark: row.remark || '',
        avg_last_year: row.avg_last_year || '',
      };
    });
    setScores(seeded);
  }, [report, month]);

  // A month's suggestion-or-kaizen must be on record before its KPI sheet is
  // accepted. Checked here only to say so before anything is typed — the
  // server refuses the submission either way.
  const monthPart = part?.months?.[month] || null;

  // The month that is open for filing, as the SERVER reckons it.
  const openPeriod = part?.current?.period || localPeriod();
  // Whether the KPI month being looked at is still the one that can be filed
  // for. A past month cannot be: the employee has to ask HR.
  const monthStillOpen = !monthPart || monthPart.period === openPeriod;

  // Jump from the KPI sheet to the form that fixes it. No month travels with
  // the jump any more — the form files for the open month and nothing else.
  const fileFor = (target) => {
    setErr('');
    setMode(target);
  };

  const submit = async () => {
    // This is the KPI sheet, not the monthly filing — the suggestion's benefit
    // is checked where the suggestion is actually sent.
    if (!card.trim()) { setErr('Please enter your card number to confirm.'); return; }
    if (monthPart && !monthPart.done) {
      setErr(`Please file a Suggestion or a Kaizen for ${monthPart.month_label} first — at least one of the two is required every month.`);
      return;
    }
    setSaving(true); setErr('');
    try {
      const rows = report?.rows || info.items || [];
      await kpiPortalApi.submit(token, {
        month, confirm_card: card,
        scores: rows.map((it) => {
          const s = scores[it.kpi_id] || {};
          return {
            kpi_id: it.kpi_id,
            self_score: Number(s.self_score) || 0,
            self_remark: s.self_remark || '',
            avg_last_year: s.avg_last_year || '',
          };
        }),
      });
      setDone(true);
      try { const r = await kpiPortalApi.report(token); setReport(r.data); } catch { /* keep the success screen */ }
    } catch (e) { setErr(errText(e, 'Could not submit. Please try again.')); }
    finally { setSaving(false); }
  };

  const setKzField = (k, v) => setKz((s) => ({ ...s, [k]: v }));
  // Photos go up inline as data URIs — small enough for a phone snap, and it
  // keeps the whole filing a single request. Video is not offered here; HR can
  // attach one later from the employee's Kaizen tab.
  const readPhoto = (side, file) => {
    if (!file) return false;
    if (!(file.type || '').startsWith('image/')) { setErr('Please choose a photo.'); return false; }
    if (file.size > MAX_PHOTO_BYTES) { setErr('That photo is too large (max 3 MB).'); return false; }
    const reader = new FileReader();
    reader.onload = () => { setErr(''); setKzField(`${side}_photo`, reader.result); };
    reader.readAsDataURL(file);
    return true;
  };

  // Pasting a copied image (Ctrl+V, or Paste on a phone keyboard) fills the
  // box the employee last touched, and failing that the first empty one — so
  // a screenshot or a photo copied from WhatsApp goes in without saving it to
  // the device first.
  const [pasteInto, setPasteInto] = useState('before');
  const onPasteImage = React.useCallback((e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const img = items.find((i) => (i.type || '').startsWith('image/'));
    if (!img) return;
    const file = img.getAsFile();
    if (!file) return;
    e.preventDefault();
    setKz((cur) => {
      const side = !cur[`${pasteInto}_photo`] ? pasteInto
        : (!cur.before_photo ? 'before' : (!cur.after_photo ? 'after' : pasteInto));
      readPhoto(side, file);
      return cur;
    });
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [pasteInto]);

  useEffect(() => {
    if (mode !== 'file') return undefined;
    window.addEventListener('paste', onPasteImage);
    return () => window.removeEventListener('paste', onPasteImage);
  }, [mode, onPasteImage]);

  // One screen, one Submit: a suggestion, a kaizen, or both. Whatever is
  // filled in is what gets filed.
  const submitFiling = async () => {
    const wantsKaizen = kaizenTouched(kz);
    if (!sugg.trim() && !wantsKaizen) {
      setErr('Write a suggestion or fill in the kaizen — either one is enough.'); return;
    }
    if (wantsKaizen) {
      if (!kz.subject.trim()) { setErr('Please give your kaizen a subject.'); return; }
      if (!kz.after_text.trim()) { setErr('Please describe the improvement you made.'); return; }
      if (!kz.before_photo) { setErr('A BEFORE photo is required — take one or paste an image.'); return; }
      if (!kz.after_photo) { setErr('An AFTER photo is required — take one or paste an image.'); return; }
      if (!kz.benefit_category) { setErr('Please choose a benefit category.'); return; }
    }
    if (sugg.trim() && !suggCat) { setErr('Please choose a benefit category for your suggestion.'); return; }
    if (!card.trim()) { setErr('Please enter your card number to confirm.'); return; }
    setSaving(true); setErr('');
    try {
      const r = await kpiPortalApi.submission(token, {
        // Always the open month. The server refuses anything else, and will
        // fill this in itself if it is left out.
        confirm_card: card, period: openPeriod,
        suggestion: sugg.trim(),
        suggestion_benefit_category: suggCat || null,
        suggestion_benefit_amount: suggCat === 'Commercial' && suggAmt !== '' ? Number(suggAmt) : null,
        // The amount box belongs to Commercial alone, and an untouched one
        // holds "". Sent as a number or as nothing — never as an empty string.
        kaizen: wantsKaizen ? {
          ...kz,
          benefit_amount: kz.benefit_category === 'Commercial' && String(kz.benefit_amount).trim() !== ''
            ? Number(kz.benefit_amount) : null,
        } : null,
      });
      setFiled(r.data);
      setDone(true);
      setSugg(''); setSuggCat(''); setSuggAmt(''); setKz(blankKaizen());
      loadHistory(); loadKaizens(); loadParticipation();
    } catch (e) { setErr(errText(e, 'Could not submit. Please try again.')); }
    finally { setSaving(false); }
  };

  // The printed HR-F-16 sheet for one of their own kaizens, opened in a new
  // tab — the same rendering HR prints.
  const openSheet = async (kid) => {
    const w = window.open('', '_blank');
    try {
      const r = await kpiPortalApi.kaizenSheet(token, kid, window.location.origin + '/assets/aampl-logo.jpg');
      if (w) { w.document.write(r.data.html); w.document.close(); }
    } catch (e) {
      if (w) w.close();
      setErr('Could not open the sheet.');
    }
  };

  // The KPI sheet needs a far wider canvas than the suggestion card.
  const wide = mode === 'report';
  const wrap = (children) => (
    <div style={{ minHeight: '100vh', background: '#F2F4F8', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 12px', fontFamily: 'Inter, Arial, sans-serif' }}>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #kpi-sheet, #kpi-sheet * { visibility: visible; }
          #kpi-sheet { position: absolute; left: 0; top: 0; width: 100%; }
          #kpi-sheet input { border: none !important; }
          .noprint { display: none !important; }
          @page { size: A4 landscape; margin: 8mm; }
        }
      `}</style>
      <div style={{ width: '100%', maxWidth: wide ? 1180 : 520, background: '#fff', borderRadius: 16, boxShadow: '0 8px 30px rgba(16,36,104,.12)', overflow: 'hidden' }}>{children}</div>
    </div>
  );
  const head = (
    <div style={{ background: 'linear-gradient(135deg,#0C1B57,#2563EB)', color: '#fff', padding: 20 }}>
      <div style={{ fontSize: 13, letterSpacing: 1, opacity: .8 }}>AAMPL · Employee Portal</div>
      {info && <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>Hi {info.name}</div>}
      {info && <div style={{ fontSize: 12, opacity: .85 }}>{info.designation} · {info.department} · {info.unit}</div>}
    </div>
  );
  const inp = { width: '100%', boxSizing: 'border-box', border: '1px solid #E6E9F0', borderRadius: 8, padding: '10px 12px', fontSize: 15 };

  if (err && !info && !gate) return wrap(<div style={{ padding: 24, color: '#C2151F' }}>{err}</div>);
  // Nothing about the employee is shown until the password gate is passed.
  if (!authed) {
    if (!gate) return wrap(<div style={{ padding: 24, color: '#5A6377' }}>Loading…</div>);
    return <PortalGate token={token} state={gate} onAuthed={() => { setErr(''); setAuthed(true); }} />;
  }
  if (!info) return wrap(<div style={{ padding: 24, color: '#5A6377' }}>Loading…</div>);
  if (done) return wrap(<>{head}
    <div style={{ padding: 28, textAlign: 'center' }}>
      <div style={{ fontSize: 44 }}>✅</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#11182B', marginTop: 8 }}>Saved — thank you!</div>
      <div style={{ fontSize: 13, color: '#5A6377', marginTop: 6 }}>
        {filed && (filed.suggestion_id || filed.kaizen_id)
          ? <>{[filed.suggestion_id && 'Suggestion', filed.kaizen_id && 'Kaizen'].filter(Boolean).join(' and ')} filed for {filed.month_label}.</>
          : <>Your submission is stored with HR and stays on your record.</>}
      </div>
      {filed?.kaizen_id && (
        <button onClick={() => openSheet(filed.kaizen_id)} data-testid="portal-filed-sheet"
          style={{ marginTop: 14, background: '#fff', color: '#11182B', border: '1px solid #D1D7E2', borderRadius: 10, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          View kaizen sheet (HR-F-16)
        </button>
      )}
      <div>
        <button onClick={() => { setDone(false); setFiled(null); setMode(null); }}
          style={{ marginTop: 16, background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer' }}>
          Back to menu
        </button>
      </div>
    </div>
  </>);

  // Suggestion or Kaizen — one of the two is required for the current month.
  const nowPart = part?.current || info.participation || null;
  const pill = (text, ok) => (
    <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap',
                   background: ok ? '#DCF3E7' : '#FDE9EF', color: ok ? '#15654B' : '#C2151F' }}>{text}</span>
  );

  // ---- landing: the three options the QR offers ----
  if (!mode) return wrap(<>{head}
    <div style={{ padding: 22 }}>
      <div style={{ fontSize: 14, color: '#5A6377', marginBottom: 12 }}>What would you like to do?</div>

      {nowPart && (
        <div data-testid="portal-participation-note"
          style={{ fontSize: 12, borderRadius: 10, padding: '10px 12px', marginBottom: 14,
                   background: nowPart.done ? '#EAF7F0' : '#FFF7E6',
                   border: `1px solid ${nowPart.done ? '#CDEBDB' : '#FDD08A'}`,
                   color: nowPart.done ? '#15654B' : '#8A5A12' }}>
          {nowPart.done
            ? <>Done for {nowPart.month_label} — {nowPart.suggestions} suggestion{nowPart.suggestions === 1 ? '' : 's'} and {nowPart.kaizens} kaizen{nowPart.kaizens === 1 ? '' : 's'} on record. You may file more.</>
            : <><b>{nowPart.month_label}:</b> a Suggestion <b>or</b> a Kaizen is required this month — file either one (or both) <b>by {periodLastDay(nowPart.period || openPeriod)}</b>. After that the month closes and your KPI sheet for it cannot be submitted.</>}
        </div>
      )}

      {/* Suggestion and Kaizen are ONE journey — the employee fills whichever
          they have on a single screen and presses Submit once. */}
      <button onClick={() => { setErr(''); setMode('file'); }} data-testid="portal-file-card"
        style={{ width: '100%', textAlign: 'left', background: '#EAF7F0', border: '1px solid #CDEBDB', borderRadius: 12, padding: 16, marginBottom: 12, cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#11182B' }}>💡🛠️ Suggestion / Kaizen</div>
          {nowPart && (nowPart.done
            ? pill(`${nowPart.suggestions + nowPart.kaizens} this month`, true)
            : pill('Required this month', false))}
        </div>
        <div style={{ fontSize: 12, color: '#5A6377', marginTop: 2 }}>
          One form for both. Write an idea, file a kaizen on the HR-F-16 sheet, or do both together.
        </div>
      </button>
      <button onClick={openReport} data-testid="portal-report-card"
        style={{ width: '100%', textAlign: 'left', background: '#EEF3FF', border: '1px solid #D7E3FF', borderRadius: 12, padding: 16, marginBottom: 12, cursor: 'pointer' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#11182B' }}>📊 KPI Report</div>
        <div style={{ fontSize: 12, color: '#5A6377', marginTop: 2 }}>Fill your Key Performance Indicator sheet.</div>
      </button>
      <button onClick={() => openMyKpi()} data-testid="portal-my-kpi-card"
        style={{ width: '100%', textAlign: 'left', background: '#F5F0FF', border: '1px solid #E2D8FA', borderRadius: 12, padding: 16, marginBottom: 12, cursor: 'pointer' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#11182B' }}>🏅 My KPI Score</div>
        <div style={{ fontSize: 12, color: '#5A6377', marginTop: 2 }}>See your month-wise marks and total score. View only — nothing can be edited here.</div>
      </button>
      {(info.manual_kpi?.pending > 0) && (
        <button onClick={openManualKpi} data-testid="portal-manual-kpi-card"
          style={{ width: '100%', textAlign: 'left', background: '#FFF6E6', border: '1px solid #F5DFB0', borderRadius: 12, padding: 16, marginBottom: 12, cursor: 'pointer' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#11182B' }}>📝 Manual KPI Work</div>
          <div style={{ fontSize: 12, color: '#5A6377', marginTop: 2 }}>
            {info.manual_kpi.pending} KPI{info.manual_kpi.pending === 1 ? '' : 's'} pending for {info.manual_kpi.employees} employee{info.manual_kpi.employees === 1 ? '' : 's'} · {info.manual_kpi.month} {info.manual_kpi.fy}
          </div>
        </button>
      )}
      {err && <div style={{ color: '#C2151F', fontSize: 13, marginTop: 12 }}>{err}</div>}
    </div>
  </>);

  const historyBlock = (
    <div style={{ marginTop: 18, borderTop: '1px solid #F2F4F8', paddingTop: 12 }}>
      <button onClick={() => setShowHistory((v) => !v)}
        style={{ background: 'none', border: 'none', padding: 0, color: '#2563EB', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
        {showHistory ? '▾' : '▸'} My previous submissions ({history.length})
      </button>
      {showHistory && (
        history.length === 0
          ? <div style={{ fontSize: 12, color: '#98A1B2', marginTop: 8 }}>Nothing submitted yet.</div>
          : <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {history.map((h) => (
                <div key={h.id} style={{ border: '1px solid #F2F4F8', borderRadius: 8, padding: '8px 10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#5A6377' }}>{h.month_label}</span>
                    <span style={{ fontSize: 10, color: h.status === 'reviewed' ? '#15A36E' : '#8A5A12' }}>
                      {h.status === 'reviewed' ? 'Reviewed by HR' : 'Received'}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: '#11182B', marginTop: 4, whiteSpace: 'pre-wrap' }}>{h.text}</div>
                  <div style={{ fontSize: 10, color: '#98A1B2', marginTop: 4 }}>Sent {formatDateTime(h.created_at)}</div>
                  {h.remark ? <div style={{ fontSize: 11, color: '#5A6377', marginTop: 4 }}>HR: {h.remark}</div> : null}
                </div>
              ))}
            </div>
      )}
    </div>
  );

  // ---- My KPI Score: strictly read-only ----
  if (mode === 'mykpi' && myKpi) {
    const st = (t) => ({ fontSize: 11, color: '#5A6377', textTransform: 'uppercase', letterSpacing: 0.4 });
    const num = { fontSize: 20, fontWeight: 800, color: '#172554' };
    const stat = (label, value) => (
      <div style={{ flex: '1 1 90px', background: '#F7F8FB', border: '1px solid #EEF1F6', borderRadius: 10, padding: '10px 12px' }}>
        <div style={st()}>{label}</div>
        <div style={num}>{value ?? '—'}</div>
      </div>
    );
    return wrap(<>
      {head}
      <div style={{ padding: 20 }}>
        <button onClick={() => setMode(null)} style={{ background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0, marginBottom: 10 }}>← Back</button>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 2 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#11182B' }}>🏅 My KPI Score</div>
          <select value={myKpi.month} onChange={(e) => openMyKpi(e.target.value)}
            style={{ border: '1px solid #D1D7E2', borderRadius: 8, padding: '4px 8px', fontSize: 13 }}>
            {myKpi.months.map((m) => <option key={m} value={m}>{m} {myKpi.fy}</option>)}
          </select>
        </div>
        <div style={{ fontSize: 12, color: '#5A6377', marginBottom: 10 }}>
          {myKpi.employee.name} · {myKpi.employee.department || '—'} · {myKpi.employee.designation || '—'} · {myKpi.employee.unit || '—'}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {stat('Total KPIs', myKpi.total_kpis)}
          {stat('Automatic', myKpi.automatic_kpis)}
          {stat('Manual', myKpi.manual_kpis)}
          {stat('Completed', myKpi.completed_kpis)}
          {stat('Pending', myKpi.pending_kpis)}
          {stat(`Weighted % (${myKpi.month})`, myKpi.monthly_weighted_pct != null ? `${myKpi.monthly_weighted_pct}%` : '—')}
          {stat('Annual %', myKpi.annual_pct != null ? `${myKpi.annual_pct}%` : '—')}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#EFF1F6', color: '#5A6377', fontSize: 10, textTransform: 'uppercase' }}>
              <th style={{ padding: '6px 6px', textAlign: 'left' }}>KPI</th>
              <th style={{ padding: '6px 4px' }}>Type</th>
              <th style={{ padding: '6px 4px' }}>Wt%</th>
              <th style={{ padding: '6px 4px' }}>Marks</th>
              <th style={{ padding: '6px 4px' }}>Wtd</th>
              <th style={{ padding: '6px 4px', textAlign: 'left' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {myKpi.rows.map((r) => (
              <tr key={`${r.sno}-${r.kpi}`} style={{ borderBottom: '1px solid #F2F4F8' }}>
                <td style={{ padding: '6px 6px', color: '#11182B' }}>{r.kpi}
                  {r.auto_actual && <div style={{ fontSize: 10, color: '#98A1B2' }}>{r.auto_actual}</div>}
                </td>
                <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: r.type === 'Automatic' ? '#2563EB' : '#15A36E' }}>{r.type.toUpperCase()}</span>
                </td>
                <td style={{ padding: '6px 4px', textAlign: 'center', color: '#5A6377' }}>{r.weight}</td>
                <td style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 700, color: '#172554' }}>{r.score ?? '—'}</td>
                <td style={{ padding: '6px 4px', textAlign: 'center', color: '#5A6377' }}>{r.weighted_contribution ?? '—'}</td>
                <td style={{ padding: '6px 4px', color: r.status === 'verified' ? '#15A36E' : '#8A5A12' }}>{r.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontSize: 11, color: '#98A1B2', marginTop: 10 }}>
          View only — marks are entered and verified by your manager / HR through the existing workflow. Weighted % uses the same formula as the HR KPI sheet.
        </div>
      </div>
    </>);
  }
  // mykpi block ends; wrap() closes the card shell above.

  // ---- the monthly filing: suggestion and/or kaizen, one screen ----
  if (mode === 'file') {
    const sheetCell = { border: '1px solid #C7CEDB', padding: '6px 8px' };
    const sheetLbl = { ...sheetCell, background: '#EAF0F6', fontWeight: 700, fontSize: 11,
                       color: '#11182B', textAlign: 'center' };
    const bare = { width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none',
                   fontSize: 14, padding: 2, background: 'transparent', fontFamily: 'inherit' };

    // Called as functions rather than rendered as components: a component
    // defined in the body is a new type on every render, so React would
    // remount the subtree and the textarea would lose focus on each keystroke.
    const cell = (label, node) => (
      <div style={{ display: 'flex', borderBottom: '1px solid #C7CEDB' }}>
        <div style={{ ...sheetLbl, width: 132, flex: 'none', display: 'flex', alignItems: 'center' }}>{label}</div>
        <div style={{ ...sheetCell, flex: 1, minWidth: 0, border: 'none' }}>{node}</div>
      </div>
    );

    const sideBox = (key, label, hint) => (
      <div key={key} style={{ flex: '1 1 220px', minWidth: 0, border: '1px solid #C7CEDB' }}>
        <div style={sheetLbl}>{label} <span style={{ color: '#C2151F' }}>*photo required</span></div>
        <div
          tabIndex={0}
          onFocus={() => setPasteInto(key)}
          onClick={() => setPasteInto(key)}
          data-testid={`portal-kaizen-${key}-box`}
          style={{ padding: 8, outline: pasteInto === key ? '2px solid #BFD8FF' : 'none', outlineOffset: -2 }}>
          {kz[`${key}_photo`] ? (
            <>
              {/* Fixed-height frame, image contained inside it. A tall phone
                  photo and a wide one then occupy the same box instead of the
                  layout jumping, and neither is cropped or stretched. */}
              <div style={{
                height: 170, width: '100%', background: '#F2F4F8', borderRadius: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden',
              }}>
                <img src={kz[`${key}_photo`]} alt={label}
                  data-testid={`portal-kaizen-${key}-img`}
                  style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto',
                    objectFit: 'contain', display: 'block' }} />
              </div>
              <button onClick={() => setKzField(`${key}_photo`, null)}
                style={{ background: 'none', border: 'none', padding: 0, marginTop: 4, color: '#C2151F', fontSize: 11, cursor: 'pointer' }}>
                Remove photo
              </button>
            </>
          ) : (
            <div style={{ border: '1px dashed #C7CEDB', borderRadius: 6, padding: '14px 8px', textAlign: 'center', background: '#FAFBFD' }}>
              <label style={{ fontSize: 13, color: '#2563EB', cursor: 'pointer', fontWeight: 600 }}>
                📷 Take / choose photo
                <input type="file" accept="image/*" style={{ display: 'none' }}
                  data-testid={`portal-kaizen-${key}-file`}
                  onChange={(e) => { readPhoto(key, e.target.files?.[0]); e.target.value = ''; }} />
              </label>
              <div style={{ fontSize: 10, color: '#98A1B2', marginTop: 4 }}>
                …or copy an image anywhere and paste it here (Ctrl+V)
              </div>
            </div>
          )}
          <textarea value={kz[`${key}_text`]} onChange={(e) => setKzField(`${key}_text`, e.target.value)}
            rows={2} placeholder={hint} data-testid={`portal-kaizen-${key}-text`}
            style={{ ...bare, resize: 'vertical', marginTop: 6, borderTop: '1px solid #EEF1F6', paddingTop: 6 }} />
        </div>
      </div>
    );

    return wrap(<>{head}
      <div style={{ padding: 18 }}>
        <button onClick={() => { setErr(''); setMode(null); }} style={{ background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 8 }}>← Back to menu</button>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#11182B' }}>Suggestion / Kaizen</div>
        <div style={{ fontSize: 12, color: '#5A6377', marginTop: 2, marginBottom: 12 }}>
          Fill <b>either one</b> — or both — and press Submit once. One of the two is
          required every month.
        </div>

        {/* No month to choose: this files for the month that is running, and
            the window shuts when the month does. */}
        <div data-testid="portal-file-period"
          style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8,
                   flexWrap: 'wrap', border: '1px solid #D7E3FF', background: '#F4F8FF',
                   borderRadius: 10, padding: '10px 12px', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#5A6377', textTransform: 'uppercase', letterSpacing: .4 }}>Filing for</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#11337A' }}>{periodLabel(openPeriod)}</div>
          </div>
          <div style={{ fontSize: 11, color: '#5A6377', textAlign: 'right' }}>
            Open until <b>{periodLastDay(openPeriod)}</b>
            <div>An earlier month cannot be filed — please speak to HR.</div>
          </div>
        </div>

        {/* ---- suggestion half ---- */}
        <div style={{ border: '1px solid #CDEBDB', background: '#F6FCF9', borderRadius: 10, padding: 12, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#15654B', marginBottom: 6 }}>💡 Suggestion</div>
          <textarea value={sugg} onChange={(e) => setSugg(e.target.value)} rows={4}
            data-testid="portal-suggestion-text"
            placeholder="Any idea or improvement you want to suggest…"
            style={{ ...inp, resize: 'vertical', background: '#fff' }} />
          {/* Asked for only once there is something to categorise, so an empty
              form is not covered in requirements before anything is typed. */}
          {sugg.trim() && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#15654B', marginBottom: 6 }}>
                What kind of benefit? <span style={{ color: '#C2151F' }}>*</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(info.benefit_categories || []).map((c) => {
                  const on = suggCat === c;
                  return (
                    <button key={c} type="button" data-testid={`sugg-benefit-${c}`}
                      onClick={() => setSuggCat(on ? '' : c)}
                      style={{
                        padding: '7px 14px', borderRadius: 999, fontSize: 13, cursor: 'pointer',
                        border: `1px solid ${on ? '#15654B' : '#CDEBDB'}`,
                        background: on ? '#15654B' : '#fff',
                        color: on ? '#fff' : '#41495B', fontWeight: on ? 600 : 400,
                      }}>
                      {c}
                    </button>
                  );
                })}
              </div>
              {suggCat === 'Commercial' && (
                <input type="number" min="0" step="any" value={suggAmt}
                  data-testid="sugg-benefit-amount"
                  onChange={(e) => setSuggAmt(e.target.value)}
                  placeholder="Estimated saving per year (₹)"
                  style={{ ...inp, background: '#fff', marginTop: 8 }} />
              )}
            </div>
          )}
        </div>

        {/* ---- kaizen half, in the shape of the printed HR-F-16 sheet ---- */}
        <div style={{ border: '1px solid #C7CEDB', borderRadius: 4, overflow: 'hidden', marginBottom: 14 }}>
          <div style={{ background: '#fff', borderBottom: '1px solid #C7CEDB', padding: '8px 10px', textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#11182B' }}>AAMPL</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#11337A', letterSpacing: .5 }}>KAIZEN SHEET</div>
            <div style={{ fontSize: 9, color: '#5A6377' }}>Doc.: HR-F-16 · Rev.: 01 · Date: 01.06.2020</div>
          </div>
          {cell('Kaizen No.', <span style={{ fontSize: 12, color: '#98A1B2' }}>HR fills this in</span>)}
          {cell('Department', <span style={{ fontSize: 14 }}>{info.department || '—'}</span>)}
          {cell('Suggested By', <span style={{ fontSize: 14 }}>{info.name}</span>)}
          {cell('Approved By', (
            <input type="text" value={kz.approved_by} onChange={(e) => setKzField('approved_by', e.target.value)}
              data-testid="portal-kaizen-approved-by" placeholder="Who approved it?" style={bare} />
          ))}
          {cell('Implemented By', (
            <input type="text" value={kz.implemented_by} onChange={(e) => setKzField('implemented_by', e.target.value)}
              data-testid="portal-kaizen-implemented-by" placeholder="Who carried it out?" style={bare} />
          ))}
          {cell('Impl. Date', (
            <input type="date" value={kz.implementation_date}
              onChange={(e) => setKzField('implementation_date', e.target.value)} style={bare} />
          ))}
          {cell('Subject', (
            <input type="text" value={kz.subject} onChange={(e) => setKzField('subject', e.target.value)}
              data-testid="portal-kaizen-subject" placeholder="What did you improve?" style={bare} />
          ))}
          {cell('Process', (
            <input type="text" value={kz.process} onChange={(e) => setKzField('process', e.target.value)}
              placeholder="Machine, line or area" style={bare} />
          ))}

          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {sideBox('before', 'Before Improvement', 'How was it done earlier? What was the problem?')}
            {sideBox('after', 'After Improvement', 'What is the new way, and what did it fix?')}
          </div>

          {/* One choice, not sixteen ticks. The categories come from the
              server so the options here are the options it will accept. */}
          <div style={sheetLbl}>Benefit <span style={{ color: '#C2151F' }}>*</span></div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: 10 }}>
            {(info.benefit_categories || []).map((c) => {
              const on = kz.benefit_category === c;
              return (
                <button key={c} type="button" data-testid={`kz-benefit-${c}`}
                  onClick={() => setKzField('benefit_category', on ? '' : c)}
                  style={{
                    padding: '7px 14px', borderRadius: 999, fontSize: 13, cursor: 'pointer',
                    border: `1px solid ${on ? '#15307F' : '#D7DCE7'}`,
                    background: on ? '#15307F' : '#fff',
                    color: on ? '#fff' : '#41495B',
                    fontWeight: on ? 600 : 400,
                  }}>
                  {c}
                </button>
              );
            })}
          </div>
          {/* The amount belongs to Commercial and only to Commercial — the
              server rejects one sent with any other category. */}
          {kz.benefit_category === 'Commercial' && (
            <div style={{ display: 'flex' }}>
              <div style={{ ...sheetLbl, width: 132, flex: 'none', display: 'flex', alignItems: 'center' }}>
                Benefit Amount
              </div>
              <div style={{ ...sheetCell, flex: 1, minWidth: 0, border: 'none' }}>
                <input type="number" min="0" step="any" value={kz.benefit_amount}
                  data-testid="kz-benefit-amount"
                  onChange={(e) => setKzField('benefit_amount', e.target.value)}
                  placeholder="Estimated saving per year (₹)" style={bare} />
              </div>
            </div>
          )}
          {cell('Benefit (notes)', (
            <input type="text" value={kz.benefit_notes} onChange={(e) => setKzField('benefit_notes', e.target.value)}
              placeholder="Optional" style={bare} />
          ))}
        </div>

        <div style={{ borderTop: '1px dashed #E6E9F0', margin: '12px 0' }} />
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#5A6377', marginBottom: 4 }}>Confirm your Card Number ({info.card_hint})</label>
        <input type="text" value={card} onChange={(e) => setCard(e.target.value)} style={inp} />
        {err && <div style={{ color: '#C2151F', fontSize: 13, margin: '10px 0' }}>{err}</div>}
        <button onClick={submitFiling} disabled={saving} data-testid="portal-file-submit"
          style={{ width: '100%', marginTop: 12, background: '#15A36E', color: '#fff', border: 'none', borderRadius: 10, padding: '14px', fontSize: 16, fontWeight: 600, cursor: 'pointer', opacity: saving ? .6 : 1 }}>
          {saving ? 'Submitting…' : 'Submit'}
        </button>

        <div style={{ marginTop: 18, borderTop: '1px solid #F2F4F8', paddingTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#5A6377', marginBottom: 8 }}>
            My previous kaizens ({kzHistory.length})
          </div>
          {kzHistory.length === 0
            ? <div style={{ fontSize: 12, color: '#98A1B2' }}>Nothing filed yet.</div>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {kzHistory.slice(0, 12).map((k) => (
                  <div key={k.id} style={{ border: '1px solid #F2F4F8', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#5A6377' }}>{k.month_label}</span>
                      {k.kaizen_no ? <span style={{ fontSize: 10, color: '#98A1B2' }}>#{k.kaizen_no}</span> : null}
                    </div>
                    <div style={{ fontSize: 13, color: '#11182B', marginTop: 4 }}>{k.subject}</div>
                    {k.process ? <div style={{ fontSize: 11, color: '#5A6377' }}>Process: {k.process}</div> : null}
                    <div style={{ fontSize: 10, color: '#98A1B2', marginTop: 4 }}>Filed {formatDateTime(k.created_at)}</div>
                    <button onClick={() => openSheet(k.id)} data-testid={`portal-kaizen-sheet-${k.id}`}
                      style={{ background: 'none', border: 'none', padding: 0, marginTop: 4, color: '#2563EB', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                      View kaizen sheet
                    </button>
                  </div>
                ))}
              </div>}
        </div>

        {historyBlock}
      </div>
    </>);
  }

  // ---- manual KPI work (what this person owes for their own people) ----
  if (mode === 'manualkpi') {
    if (!mk) return wrap(<>{head}<div style={{ padding: 24, color: '#5A6377' }}>Opening your KPI work…</div></>);
    const stat = (label, value, colour) => (
      <div style={{ flex: '1 1 110px', border: '1px solid #E6E9F0', borderRadius: 10, padding: '8px 10px' }}>
        <div style={{ fontSize: 11, color: '#5A6377' }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: colour || '#11182B' }}>{value}</div>
      </div>
    );
    const dirty = Object.values(mkEdits).filter((v) => v !== '' && v != null).length;
    return wrap(<>{head}
      <div style={{ padding: 18 }}>
        <button onClick={() => { setErr(''); setMode(null); }} style={{ background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 8 }}>← Back to menu</button>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#11182B' }}>📝 Manual KPI Work</div>
        <div style={{ fontSize: 12, color: '#5A6377', marginTop: 2 }}>FY {mk.fy} · {mk.month}</div>
        {mk.month_frozen && (
          <div style={{ marginTop: 10, background: '#FDE9EF', border: '1px solid #F6C9D6', borderRadius: 8, padding: '8px 10px', fontSize: 12, color: '#C2151F' }}>
            This month is published and locked. Nothing can be changed.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '14px 0' }} data-testid="manual-kpi-summary">
          {stat('Employees', mk.employee_count)}
          {stat('Completed', mk.completed, '#15A36E')}
          {stat('Pending', mk.total_pending, mk.total_pending ? '#C2151F' : '#15A36E')}
        </div>

        {mk.employees.length === 0 || (mk.total_pending === 0) ? (
          <div style={{ background: '#EAF7F0', border: '1px solid #CDEBDB', borderRadius: 10, padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 30 }}>✅</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#11182B', marginTop: 4 }}>Nothing pending</div>
            <div style={{ fontSize: 12, color: '#5A6377', marginTop: 2 }}>All manual KPIs you are responsible for are filled.</div>
          </div>
        ) : mk.employees.map((e) => (
          <div key={e.employee_id} style={{ border: '1px solid #E6E9F0', borderRadius: 12, marginBottom: 12, overflow: 'hidden' }}>
            <div style={{ background: '#F7F9FC', padding: '10px 12px' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#11182B' }}>{e.name}</div>
              <div style={{ fontSize: 11, color: '#5A6377' }}>{e.designation} · {e.department}</div>
              <div style={{ fontSize: 11, color: e.pending ? '#8A5A12' : '#15A36E', marginTop: 2 }}>
                {e.pending} pending · {e.completed} done
              </div>
            </div>
            <div style={{ padding: '4px 12px 10px' }}>
              {e.kpis.map((k) => {
                const key = `${e.employee_id}|${k.kpi_id}`;
                return (
                  <div key={k.kpi_id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 0', borderTop: '1px solid #F2F4F8' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: '#11182B' }}>{k.kpi}</div>
                      <div style={{ fontSize: 10, color: '#98A1B2' }}>
                        {k.weight ? `Weight ${k.weight}%` : ''}
                        {k.mode === 'auto' ? ' · filled by the software' : ''}
                        {k.mode === 'manual' && k.done ? ' · already marked' : ''}
                      </div>
                    </div>
                    {k.editable ? (
                      <input type="number" min={0} max={mk.max} step="0.5"
                        data-testid={`manual-kpi-score-${k.kpi_id}`}
                        value={mkEdits[key] ?? ''}
                        onChange={(ev) => setMkEdits((s) => ({ ...s, [key]: ev.target.value }))}
                        placeholder={`0–${mk.max}`}
                        style={{ width: 84, boxSizing: 'border-box', border: '1px solid #D7E3FF', borderRadius: 8, padding: '8px 10px', fontSize: 15, textAlign: 'center' }} />
                    ) : (
                      <span style={{ width: 84, textAlign: 'center', fontSize: 11, fontWeight: 700, color: k.mode === 'auto' ? '#2563EB' : '#15A36E' }}>
                        {k.mode === 'auto' ? 'AUTO' : (k.done ? 'DONE' : 'LOCKED')}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {err && <div style={{ color: '#C2151F', fontSize: 13, margin: '10px 0' }}>{err}</div>}
        {mk.total_pending > 0 && !mk.month_frozen && (
          <>
            <button onClick={submitManualKpi} disabled={saving || !dirty} data-testid="manual-kpi-submit"
              style={{ width: '100%', marginTop: 8, background: dirty ? '#15A36E' : '#C7CEDB', color: '#fff', border: 'none', borderRadius: 10, padding: 14, fontSize: 16, fontWeight: 600, cursor: dirty ? 'pointer' : 'default', opacity: saving ? .6 : 1 }}>
              {saving ? 'Saving…' : `Submit ${dirty || ''} score${dirty === 1 ? '' : 's'}`.replace('  ', ' ')}
            </button>
            <div style={{ fontSize: 11, color: '#98A1B2', marginTop: 6, textAlign: 'center' }}>
              You can fill some now and the rest later — nothing is lost.
            </div>
          </>
        )}
      </div>
    </>);
  }

  // ---- report (the KPI sheet) ----
  if (!report) return wrap(<>{head}<div style={{ padding: 24, color: '#5A6377' }}>Opening your KPI sheet…</div></>);

  const rows = report.rows || [];
  const maxv = report.max || 4;
  const months = report.months || [];
  const locked = report.locked;

  const monthLabels = report.month_labels || months;
  // Row tints, in the order the printed sheet uses them.
  const BAND = ['#DEE7F5', '#FCE9E1', '#E8F3E4', '#FDF3D8', '#E8F3E4', '#DEE7F5'];

  // The letterhead block: logo, company, title, and the DOC/REV boxes.
  const sheetHead = (
    <div style={{ display: 'flex', border: '1px solid #7A879E', borderBottom: 'none' }}>
      <div style={{ width: 74, flex: 'none', borderRight: '1px solid #7A879E', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 4 }}>
        <img src="/assets/aampl-logo.jpg" alt="AAMPL" style={{ maxWidth: '100%', maxHeight: 46, objectFit: 'contain' }}
             onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ textAlign: 'center', padding: '5px 6px', borderBottom: '1px solid #7A879E' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#1B3FAE', letterSpacing: .4 }}>{report.company}</div>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#1B3FAE' }}>{report.company_city}</div>
        </div>
        <div style={{ textAlign: 'center', padding: '4px 6px', fontSize: 13, fontWeight: 800, color: '#C2151F', borderBottom: '1px solid #7A879E' }}>
          {report.title}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', fontSize: 10, fontWeight: 700 }}>
          <div style={{ flex: '1 1 240px', background: '#FDE9EF', color: '#C2151F', padding: '4px 6px' }}>
            DESIGNATION - {(report.designation || '—').toUpperCase()}
          </div>
          <div style={{ flex: '1 1 130px', padding: '4px 6px', color: '#11182B' }}>NAME : {report.name}</div>
          <div style={{ flex: '1 1 110px', padding: '4px 6px', color: '#11182B' }}>YEAR : {report.fy_label}</div>
        </div>
      </div>
      <div style={{ width: 96, flex: 'none', borderLeft: '1px solid #7A879E' }}>
        {['DATED.', 'REV. NO.', 'REV.DATE'].map((l, i) => (
          <div key={l} style={{ display: 'flex', fontSize: 8, fontWeight: 700, color: '#11182B',
                                borderTop: i ? '1px solid #C7CEDB' : 'none' }}>
            <span style={{ flex: 1, padding: '4px 4px' }}>{l}</span>
            <span style={{ width: 34, borderLeft: '1px solid #C7CEDB' }} />
          </div>
        ))}
      </div>
    </div>
  );

  return wrap(<>{head}
    <div style={{ padding: 14 }}>
      <div className="noprint" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <button onClick={() => { setErr(''); setMode(null); }} style={{ background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer', fontSize: 13, padding: 0 }}>← Back to menu</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#5A6377' }}>Filling</span>
          <select value={month} onChange={(e) => setMonth(e.target.value)}
            style={{ border: '1px solid #E6E9F0', borderRadius: 8, padding: '6px 8px', fontSize: 14, fontWeight: 700 }}>
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      <div className="noprint" style={{ fontSize: 11, color: '#5A6377', marginBottom: 8 }}>
        Type into the highlighted <b>{month}</b> column. Scroll sideways to see the whole year.
        {' '}<b>Avegare last year</b> is entered once for the year and then locked.
      </div>

      {locked && <div className="noprint" style={{ fontSize: 12, color: '#B14A0E', background: '#FFF7E6', border: '1px solid #FDD08A', borderRadius: 8, padding: 8, marginBottom: 10 }}>
        Your KPI list is locked for this year. Entries are still recorded for HR.
      </div>}

      {monthPart && !monthPart.done && (
        <div className="noprint" data-testid="portal-kpi-blocked"
          style={{ fontSize: 12, color: '#C2151F', background: '#FDE9EF', border: '1px solid #F6C9D6', borderRadius: 8, padding: 10, marginBottom: 10 }}>
          <b>{monthPart.month_label} is missing its Suggestion or Kaizen.</b> One of the two is required
          every month, and this sheet cannot be submitted until it is filed.
          {/* Only offered while that month is still open. Sending someone to a
              form that would refuse their month is worse than telling them. */}
          <div style={{ marginTop: 6 }}>
            {monthStillOpen ? (
              <button onClick={() => fileFor('file')} data-testid="portal-kpi-blocked-file"
                style={{ background: 'none', border: 'none', padding: 0, color: '#2563EB', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>
                File a suggestion or kaizen now
              </button>
            ) : (
              <span data-testid="portal-kpi-blocked-closed" style={{ color: '#8A5A12' }}>
                {monthPart.month_label} is closed — only HR can record it now. Please speak to HR.
              </span>
            )}
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div style={{ color: '#C2151F', fontSize: 14, padding: '12px 0' }}>No KPIs are assigned to you yet. Please contact HR.</div>
      ) : (
        <div id="kpi-sheet">
          {sheetHead}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 9, minWidth: 1000, width: '100%' }}>
              <thead>
                <tr style={{ background: '#E9EDF4' }}>
                  <th style={thS(28)}>S.No</th>
                  <th style={thS(90)}>KPI<br />DESCRIPTION</th>
                  <th style={thS(115)}>OBJECTIVE</th>
                  <th style={thS(140)}>CRITERIA</th>
                  <th style={{ ...thS(54), background: '#DCE3EF' }}>AVEGARE<br />LAST YEAR</th>
                  {months.map((m, i) => (
                    <th key={m} style={{ ...thS(44), color: '#1B3FAE', background: m === month ? '#FFF0CC' : undefined }}>
                      {monthLabels[i] || m}
                    </th>
                  ))}
                  <th style={thS(50)}>AVEGARE</th>
                  <th style={thS(70)}>REMARKS</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const tint = BAND[idx % BAND.length];
                  const s = scores[row.kpi_id] || {};
                  return (
                    <tr key={row.kpi_id} style={{ height: 58 }}>
                      <td style={{ ...tdS(), background: tint, fontWeight: 700 }}>{row.sno}</td>
                      <td style={{ ...tdS(), background: tint, fontWeight: 700 }}>{row.kpi}</td>
                      <td style={{ ...tdS(), background: tint }}>{row.objective}</td>
                      {/* Some sheets print one marking rule per line — keep the breaks. */}
                      <td style={{ ...tdS(), background: tint, whiteSpace: 'pre-line' }}>{row.criteria}</td>
                      {/* Baseline is entered once for the year, then frozen. */}
                      <td style={{ ...tdS(), background: '#DCE3EF', padding: row.avg_last_year_locked ? 3 : 0,
                                   fontWeight: row.avg_last_year_locked ? 700 : 400 }}
                          title={row.avg_last_year_locked ? 'Set for this year — cannot be changed' : undefined}>
                        {row.avg_last_year_locked ? row.avg_last_year : (
                          <input type="text" value={s.avg_last_year ?? ''}
                            onChange={(e) => setScore(row.kpi_id, 'avg_last_year', e.target.value)}
                            placeholder="—"
                            aria-label={`Average last year for ${row.kpi} (can only be set once)`}
                            style={{ width: '100%', boxSizing: 'border-box', border: 'none', background: 'transparent',
                                     padding: '5px 3px', fontSize: 10, textAlign: 'center', color: '#11182B', outlineOffset: -2 }} />
                        )}
                      </td>
                      {months.map((m) => {
                        const c = (row.cells || {})[m] || {};
                        if (m === month) {
                          return (
                            <td key={m} style={{ ...tdS(), background: '#FFF8E3', padding: 0 }}>
                              <input type="number" min={0} max={maxv} step={0.5}
                                value={s.self_score ?? ''}
                                onChange={(e) => setScore(row.kpi_id, 'self_score', e.target.value)}
                                aria-label={`${row.kpi} — marks for ${m}`}
                                style={{ width: '100%', boxSizing: 'border-box', border: 'none', background: 'transparent',
                                         padding: '5px 3px', fontSize: 11, textAlign: 'center', color: '#11182B',
                                         fontWeight: 700, outlineOffset: -2 }} />
                            </td>
                          );
                        }
                        return <td key={m} style={{ ...tdS(), fontWeight: 700 }}>{c.marks ?? ''}</td>;
                      })}
                      <td style={{ ...tdS(), background: tint, fontWeight: 700 }}>{row.average ?? ''}</td>
                      <td style={{ ...tdS(), background: tint, padding: 0 }}>
                        <input type="text" value={s.self_remark ?? ''}
                          onChange={(e) => setScore(row.kpi_id, 'self_remark', e.target.value)}
                          aria-label={`Remarks for ${row.kpi}`}
                          style={{ width: '100%', boxSizing: 'border-box', border: 'none', background: 'transparent',
                                   padding: '5px 4px', fontSize: 9, color: '#11182B', outlineOffset: -2 }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="noprint">
        <div style={{ borderTop: '1px dashed #E6E9F0', margin: '14px 0 10px' }} />
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#5A6377', marginBottom: 4 }}>Confirm your Card Number ({report.card_hint})</label>
        <input type="text" value={card} onChange={(e) => setCard(e.target.value)} style={inp} />
        {err && <div style={{ color: '#C2151F', fontSize: 13, margin: '10px 0' }}>{err}</div>}
        {(() => {
          const blocked = saving || rows.length === 0 || !!(monthPart && !monthPart.done);
          return (
            <button onClick={submit} disabled={blocked} data-testid="portal-kpi-submit"
              style={{ width: '100%', marginTop: 12, background: '#2563EB', color: '#fff', border: 'none', borderRadius: 10, padding: 14, fontSize: 16, fontWeight: 600, cursor: blocked ? 'default' : 'pointer', opacity: blocked ? .6 : 1 }}>
              {saving ? 'Submitting…' : `Submit ${month} report`}
            </button>
          );
        })()}
        <button onClick={() => window.print()}
          style={{ width: '100%', marginTop: 8, background: '#fff', color: '#11182B', border: '1px solid #D1D7E2', borderRadius: 10, padding: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          Print / Save as PDF
        </button>
        <div style={{ fontSize: 11, color: '#98A1B2', textAlign: 'center', marginTop: 10 }}>KPIs measured by the plant software are filled in automatically each month.</div>
      </div>
    </div>
  </>);
}

// Shared cell styling for the KPI sheet.
const thS = (w) => ({
  border: '1px solid #7A879E', padding: '4px 3px', fontSize: 9, fontWeight: 700,
  color: '#11182B', minWidth: w, textAlign: 'center', whiteSpace: 'nowrap',
});
const tdS = () => ({
  border: '1px solid #A9B2C3', padding: '3px', fontSize: 9,
  color: '#11182B', textAlign: 'center', verticalAlign: 'middle',
});
